import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Link, useLocation } from 'react-router-dom';
import { KIND_LABEL, SOURCE_LABEL, bySection, dayWords, dueState, dueWords, progress } from '../../lib/people';
import { relative } from '../../lib/when';
import { tagText, deviceName } from '../../lib/assets';
import { useStaff } from './RunsList';
import { useMe } from '../../lib/me';
import RunLicences from '../../components/licences/RunLicences';
import { cameFrom } from '../../components/asset/BackLink';

const API = process.env.REACT_APP_API_URL;
const errorOf = (e, fallback) => e?.response?.data?.error || fallback;
const nameOf = (u) => u.display_name || u.email;

/**
 * WHAT IS STILL WITH THEM, live from the register and the approvals - the
 * leaver half of Asset Portal -> Leavers folded into the checklist, and for a
 * joiner what has been issued. Administrators only (the server sends no kit
 * to anybody else).
 */
function Kit({ run, kit }) {
  const leaver = run.kind === 'leaver';
  const acct = kit.account || {};
  const lastSeen = acct.last_login_at || acct.last_seen_at;
  const covering = kit.covering || [];
  const waiting = kit.approvals?.tickets || [];
  const hours = Number(kit.approvals?.hours) || 0;
  const nothing = !kit.devices.length && !kit.desks.length && !waiting.length && !hours && !covering.length
    && !(kit.licences?.seats?.length);
  return (
    <section className="pp-card pp-kit" aria-label={leaver ? 'Still with them' : 'Issued to them'}>
      <h2 className="pp-card-title">{leaver ? 'Still with them' : 'Issued to them'}</h2>
      <dl className="pp-facts">
        <div>
          <dt>Jarvis account</dt>
          <dd data-account={acct.active === false ? 'off' : 'on'}>
            {acct.active === false ? 'Deactivated' : 'Active'}
            {acct.is_admin ? ' · administrator' : ''}
          </dd>
        </div>
        <div>
          <dt>Last signed in</dt>
          <dd>{lastSeen ? relative(lastSeen) : 'Never'}</dd>
        </div>
      </dl>
      {kit.devices.length > 0 && (
        <>
          <h3 className="pp-sub">{kit.devices.length === 1 ? 'A device' : `${kit.devices.length} devices`}</h3>
          <ul className="pp-kit-list" aria-label="Devices">
            {kit.devices.map((d) => (
              <li key={d.id} data-device={d.id}>
                <Link to={`/assets/${d.id}`} state={cameFrom('Checklist')}><strong>{tagText(d)}</strong> {deviceName(d)}</Link>
                <span className="pp-quiet">{d.category_name}{d.location_path ? ` · ${d.location_path}` : ''}</span>
              </li>
            ))}
          </ul>
          {leaver && <p className="pp-help">Open each and use Take back. It drops off this list once it is back.</p>}
        </>
      )}
      {kit.desks.length > 0 && (
        <>
          <h3 className="pp-sub">{kit.desks.length === 1 ? 'Their desk' : 'Their desks'}</h3>
          <ul className="pp-kit-list" aria-label="Desks">
            {kit.desks.map((d) => (
              <li key={d.id}>
                <Link to={`/assets/desks/${d.id}`} state={cameFrom('Checklist')}><strong>{d.name}</strong></Link>
                <span className="pp-quiet">{d.path}</span>
              </li>
            ))}
          </ul>
          {leaver && <p className="pp-help">Give it to somebody else or set it back to unassigned from the desk&rsquo;s page.</p>}
        </>
      )}
      {(waiting.length > 0 || hours > 0) && (
        <>
          <h3 className="pp-sub">Waiting for their approval</h3>
          <ul className="pp-kit-list" aria-label="Approvals waiting">
            {waiting.map((t) => (
              <li key={t.id}><Link to={`/tickets/${t.id}`}><strong>{t.ticket_number}</strong> {t.title}</Link></li>
            ))}
            {hours > 0 && <li>{hours} hour sign-off{hours === 1 ? '' : 's'} named to them</li>}
          </ul>
          {leaver && <p className="pp-help">Name another approver on these before they go, or set them away with a delegate.</p>}
        </>
      )}
      {covering.length > 0 && (
        <>
          <h3 className="pp-sub">Covering approvals for</h3>
          <ul className="pp-kit-list">
            {covering.map((c) => <li key={c.id}>{c.name}, {dayWords(c.starts_on)} to {dayWords(c.ends_on)}</li>)}
          </ul>
        </>
      )}
      {nothing && (
        <p className="pp-quiet" data-kit-empty="">
          {leaver ? 'Nothing in Jarvis is still with them: no devices, no desk, no approvals.' : 'Nothing issued to them in Jarvis yet.'}
        </p>
      )}
    </section>
  );
}

/** One step: the tick, what it says, when, who, and its note. */
function Step({ step, today, can, me, staff, busy, onChange }) {
  const [noting, setNoting] = useState(false);
  const [note, setNote] = useState(step.note || '');
  const state = dueState(step, today);
  const mine = can.manage || step.assignee_id === me;
  const done = step.state !== 'todo';
  const open = can.open;

  useEffect(() => { setNote(step.note || ''); }, [step.note]);

  return (
    <li className={`pp-step is-${step.state}${state ? ` is-${state}` : ''}`} data-step={step.step_key || step.id}>
      <label className="pp-tick">
        <input type="checkbox" checked={done} disabled={!open || !mine || busy}
               aria-label={`${done ? 'Untick' : 'Tick'}: ${step.title}`}
               onChange={() => onChange(step, { state: done ? 'todo' : 'done' })} />
      </label>
      <div className="pp-step-body">
        <div className="pp-step-title">
          <span>{step.title}</span>
          {step.state === 'skipped' && <span className="pp-chip">Not needed</span>}
        </div>
        {step.notes && <p className="pp-step-notes">{step.notes}</p>}
        <div className="pp-step-meta">
          <span className={`pp-due${state ? ` is-${state}` : ''}`} data-due={step.due_on || ''}>
            {state === 'overdue' ? `Overdue · ${dueWords(step.due_on)}` : dueWords(step.due_on)}
          </span>
          {can.manage && staff.length > 0 && open ? (
            <select className="pp-assignee" value={step.assignee_id || ''} disabled={busy}
                    aria-label={`Who does: ${step.title}`}
                    onChange={(e) => onChange(step, { assignee_id: e.target.value || null })}>
              <option value="">Nobody in particular</option>
              {staff.map((u) => <option key={u.id} value={u.id}>{nameOf(u)}</option>)}
            </select>
          ) : (
            step.assignee_name && <span className="pp-quiet">{step.assignee_name}</span>
          )}
          {done && step.done_by_name && (
            <span className="pp-quiet">{step.state === 'done' ? 'Done' : 'Marked not needed'} by {step.done_by_name}, {relative(step.done_at)}</span>
          )}
          {open && mine && (
            <span className="pp-step-tools">
              {step.state === 'todo' && (
                <button type="button" className="pp-link" disabled={busy} onClick={() => onChange(step, { state: 'skipped' })}>Not needed</button>
              )}
              <button type="button" className="pp-link" aria-expanded={noting} onClick={() => setNoting((v) => !v)}>
                {step.note ? 'Edit note' : 'Add a note'}
              </button>
            </span>
          )}
        </div>
        {step.note && !noting && <p className="pp-step-note">{step.note}</p>}
        {noting && (
          <form className="pp-note-form" onSubmit={(e) => { e.preventDefault(); onChange(step, { note }).then((ok) => { if (ok) setNoting(false); }); }}>
            <textarea value={note} maxLength={2000} rows={2} aria-label={`Note on: ${step.title}`}
                      onChange={(e) => setNote(e.target.value)} />
            <div className="pp-actions">
              <button type="submit" className="btn btn-small btn-primary" disabled={busy}>Save note</button>
              <button type="button" className="btn btn-small btn-secondary" onClick={() => { setNote(step.note || ''); setNoting(false); }}>Cancel</button>
            </div>
          </form>
        )}
      </div>
    </li>
  );
}

export default function RunDetail({ id, isAdmin }) {
  const location = useLocation();
  const [data, setData] = useState(null);
  const [problem, setProblem] = useState('');
  const [said, setSaid] = useState(location.state?.created === false ? 'They already had this checklist open - here it is.' : '');
  const [busy, setBusy] = useState(false);
  const [closing, setClosing] = useState(null);     // the server's "close anyway?" sentence
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState({ title: '', section: '', due_on: '' });
  const [off, setOff] = useState(false);            // the deactivate question
  const me = useMe()?.id || null;
  const staffAll = useStaff(isAdmin);
  const staff = useMemo(() => staffAll.filter((u) => u.active !== false)
    .sort((a, b) => nameOf(a).localeCompare(nameOf(b))), [staffAll]);

  const load = useCallback(() => axios.get(`${API}/people/runs/${id}`)
    .then((r) => { setData(r.data); setProblem(''); })
    .catch((e) => { setProblem(errorOf(e, 'Could not load the checklist.')); setData((d) => d || false); }), [id]);
  useEffect(() => { load(); }, [load]);

  const today = useMemo(() => new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Europe/Malta', year: 'numeric', month: '2-digit', day: '2-digit' }).format(new Date()), []);

  const patchRun = async (body, message) => {
    setBusy(true); setProblem(''); setSaid('');
    try {
      const r = await axios.patch(`${API}/people/runs/${id}`, body);
      setData(r.data); setClosing(null);
      if (message) setSaid(message);
    } catch (e) {
      if (e?.response?.status === 409 && e.response.data?.needs_force) setClosing({ ...e.response.data, status: body.status });
      else setProblem(errorOf(e, 'That did not work.'));
    } finally { setBusy(false); }
  };

  const patchStep = async (step, body) => {
    setBusy(true); setProblem('');
    try {
      const r = await axios.patch(`${API}/people/runs/${id}/steps/${step.id}`, body);
      setData(r.data);
      return true;
    } catch (e) {
      setProblem(errorOf(e, 'That step could not be changed.'));
      return false;
    } finally { setBusy(false); }
  };

  const addStep = async (e) => {
    e.preventDefault();
    if (!draft.title.trim()) return;
    setBusy(true); setProblem('');
    try {
      const r = await axios.post(`${API}/people/runs/${id}/steps`, {
        title: draft.title.trim(), section: draft.section || null, due_on: draft.due_on || null });
      setData(r.data); setAdding(false); setDraft({ title: '', section: '', due_on: '' });
    } catch (err) {
      setProblem(errorOf(err, 'Could not add the step.'));
    } finally { setBusy(false); }
  };

  const deactivate = async () => {
    setBusy(true); setProblem(''); setOff(false);
    try {
      await axios.delete(`${API}/users/${data.run.user_id}`, { data: { leaving_date: data.run.day } });
      setSaid(`${data.run.person_name} is deactivated and signed out of Jarvis.`);
      await load();
    } catch (e) {
      setProblem(errorOf(e, 'Could not deactivate them.'));
    } finally { setBusy(false); }
  };

  if (data === null) return <p className="pp-quiet">Loading…</p>;
  if (data === false) {
    return (
      <div className="empty-state pp-empty">
        <p>{problem || 'That checklist does not exist.'}</p>
        <p><Link to="/people">← All checklists</Link></p>
      </div>
    );
  }

  const { run, steps, kit } = data;
  const can = { ...data.can, open: run.status === 'open' };
  const p = progress(run);
  const sections = bySection(steps);
  const sectionNames = sections.map((s) => s.name).filter((n) => n !== 'Other');

  return (
    <div className="pp-detail" data-run={run.id} data-kind={run.kind} data-status={run.status}>
      <p className="pp-back"><Link to="/people">← All checklists</Link></p>
      <header className="pp-card pp-run-head">
        <div className="pp-run-headline">
          <span className={`pp-kind is-${run.kind}`}>{KIND_LABEL[run.kind]}</span>
          <h2 className="pp-person">{run.person_name}</h2>
          {run.status !== 'open' && (
            <span className={`badge ${run.status === 'done' ? 'badge-resolved' : 'badge-closed'}`} data-run-status="">
              {run.status === 'done' ? 'Done' : 'Cancelled'}
            </span>
          )}
        </div>
        <p className="pp-quiet pp-email">{run.person_email}{run.person_department ? ` · ${run.person_department}` : ''}</p>
        <dl className="pp-facts">
          <div>
            <dt>{run.kind === 'joiner' ? 'Starts' : 'Leaving'}</dt>
            <dd>
              {can.manage && can.open ? (
                <input type="date" className="pp-day" value={run.day} disabled={busy}
                       aria-label={run.kind === 'joiner' ? 'Start date' : 'Leaving date'}
                       onChange={(e) => e.target.value && patchRun({ day: e.target.value },
                         'The date moved, and so did every step still to do.')} />
              ) : dayWords(run.day)}
            </dd>
          </div>
          <div>
            <dt>Owner</dt>
            <dd>
              {can.admin && staff.length > 0 ? (
                <select value={run.owner_id || ''} disabled={busy} aria-label="Owner"
                        onChange={(e) => patchRun({ owner_id: e.target.value || null })}>
                  <option value="">Nobody in particular</option>
                  {staff.map((u) => <option key={u.id} value={u.id}>{nameOf(u)}</option>)}
                </select>
              ) : (run.owner_name || 'Nobody in particular')}
            </dd>
          </div>
          <div>
            <dt>Progress</dt>
            <dd>
              <span className="pp-bar is-wide" role="progressbar" aria-valuemin={0} aria-valuemax={p.total} aria-valuenow={p.done}
                    aria-label={`${p.label} steps done`}>
                <span className="pp-bar-fill" style={{ width: `${Math.round(p.fraction * 100)}%` }} />
              </span>
              <span className="pp-run-count">{p.label}</span>
            </dd>
          </div>
        </dl>
        <p className="pp-quiet pp-source">
          {SOURCE_LABEL[run.source] || 'Started'}{run.started_by_name ? ` by ${run.started_by_name}` : ''}, {relative(run.started_at)}
          {run.closed_at ? ` · ${run.status === 'done' ? 'closed' : 'cancelled'} by ${run.closed_by_name || 'somebody'}, ${relative(run.closed_at)}` : ''}
        </p>
        {run.note && <p className="pp-run-note">{run.note}</p>}
        {can.manage && (
          <div className="pp-actions pp-run-actions">
            {can.open ? (
              <>
                <button type="button" className="btn btn-primary" disabled={busy}
                        onClick={() => patchRun({ status: 'done' }, 'Closed as done.')}>Close as done</button>
                <button type="button" className="btn btn-secondary" disabled={busy}
                        onClick={() => patchRun({ status: 'cancelled', force: true }, 'Cancelled.')}>Cancel checklist</button>
              </>
            ) : (
              <button type="button" className="btn btn-secondary" disabled={busy}
                      onClick={() => patchRun({ status: 'open' }, 'Open again.')}>Open again</button>
            )}
            {can.admin && run.kind === 'leaver' && run.person_active !== false && !off && (
              <button type="button" className="btn btn-danger" disabled={busy} onClick={() => setOff(true)}>Deactivate their account</button>
            )}
          </div>
        )}
        {off && (
          <div className="pp-confirm" role="group" aria-label={`Deactivate ${run.person_name}?`} data-confirm-off="">
            <p>Deactivate {run.person_name}? They are signed out of Jarvis straight away. Their tickets and history are kept.</p>
            <div className="pp-actions">
              <button type="button" className="btn btn-small btn-danger" onClick={deactivate}>Deactivate</button>
              <button type="button" className="btn btn-small btn-secondary" onClick={() => setOff(false)}>Cancel</button>
            </div>
          </div>
        )}
        {closing && (
          <div className="pp-confirm" role="group" aria-label="Close anyway?" data-confirm-close="">
            <p>{closing.error}</p>
            <div className="pp-actions">
              <button type="button" className="btn btn-small btn-primary" disabled={busy}
                      onClick={() => patchRun({ status: closing.status, force: true }, 'Closed as done.')}>Close anyway</button>
              <button type="button" className="btn btn-small btn-secondary" onClick={() => setClosing(null)}>Not yet</button>
            </div>
          </div>
        )}
      </header>

      <p className="pp-flash" role="status" aria-live="polite">{said}</p>
      {problem && <p className="pp-problem" role="alert">{problem}</p>}

      <div className="pp-detail-grid">
        <section className="pp-card pp-checklist" aria-label="Checklist">
          <h2 className="pp-card-title">Checklist</h2>
          {sections.map((sec) => (
            <div key={sec.name} className="pp-section-block">
              <h3 className="pp-sub">{sec.name}</h3>
              <ul className="pp-steps">
                {sec.steps.map((s) => (
                  <Step key={s.id} step={s} today={today} can={can} me={me} staff={staff} busy={busy} onChange={patchStep} />
                ))}
              </ul>
            </div>
          ))}
          {can.manage && can.open && (adding ? (
            <form className="pp-add" onSubmit={addStep} aria-label="Add a step">
              <label className="pp-field pp-wide">
                <span>Step</span>
                <input type="text" value={draft.title} maxLength={300} required
                       onChange={(e) => setDraft({ ...draft, title: e.target.value })} />
              </label>
              <label className="pp-field">
                <span>Section</span>
                <select value={draft.section} onChange={(e) => setDraft({ ...draft, section: e.target.value })}>
                  <option value="">Other</option>
                  {sectionNames.map((n) => <option key={n} value={n}>{n}</option>)}
                </select>
              </label>
              <label className="pp-field">
                <span>Due</span>
                <input type="date" value={draft.due_on} onChange={(e) => setDraft({ ...draft, due_on: e.target.value })} />
              </label>
              <div className="pp-actions">
                <button type="submit" className="btn btn-small btn-primary" disabled={busy || !draft.title.trim()}>Add step</button>
                <button type="button" className="btn btn-small btn-secondary" onClick={() => setAdding(false)}>Cancel</button>
              </div>
            </form>
          ) : (
            <button type="button" className="btn btn-small btn-secondary pp-add-btn" onClick={() => setAdding(true)}>Add a step</button>
          ))}
        </section>
        {kit && <Kit run={run} kit={kit} />}
        {/* 119: their licence seats - free them (leaver), give the standard
            ones (joiner). Only when the kit has a licences part. */}
        {kit && kit.licences && <RunLicences run={run} licences={kit.licences} onChanged={load} />}
      </div>
    </div>
  );
}
